import { Component, OnInit, ViewChild } from '@angular/core';
import { Log } from 'ng2-logger';
import { PlantManagementComponent } from '@app/roles/admin/pages/plants/plants.component';
import { InsertionCycle } from '@app/shared/models/insertion-cycle.model';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { InsertionCyclesService } from '@app/core/services/insertion-cycles.service';
import { PermissionService } from '@app/authentication/services/permission.service';
import { PlantCreateModalComponent } from '@app/roles/admin/pages/plants/components/plant-create-modal/plant-create-modal.component';
import { Line } from '@app/shared/models/line.model';
import { LineService } from '@app/core/services/line.service';
import { InsertionCyclesCreateModalComponent } from './components/insertion-cycles-create-modal/insertion-cycles-create-modal.component';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService, TranslateParser } from '@ngx-translate/core';

@Component({
    selector: 'app-insertion-cycles',
    templateUrl: './insertion-cycles.component.html',
    styleUrls: ['./insertion-cycles.component.scss']
})
export class InsertionCyclesComponent implements OnInit {
    _logger = Log.create(InsertionCyclesComponent.name);

    @ViewChild(InsertionCyclesCreateModalComponent, {static: true}) insertionCycleModal;

    insertionCycles: InsertionCycle[];
    lines: Line[] = [];
    selectedInsertionCycle: InsertionCycle = new InsertionCycle();
    tableConfig = new TableConfig();
    isLoadingInsertionCycles = true;
    isModalLoading = false;
    hasEditPermissions = false;

    constructor(private insertionCyclesService: InsertionCyclesService,
                private toastr: ToastsManager,
                private translationService: TranslateService,
                private translateParser: TranslateParser,
                private permissionService: PermissionService) {
    }

    ngOnInit() {
        this._logger.info("Initialize");
        this.getInsertionCycles();
        this.hasEditPermissions = this.permissionService.hasHasPermissionForFeature('MANAGE_INSERTION_CYCLES');
    }

    public onTableConfigChange() {
        this.getInsertionCycles();
    }

    public openCreateInsertionCycleModal() {
        this.selectedInsertionCycle = new InsertionCycle();
        this.insertionCycleModal.open(this.selectedInsertionCycle);
    }

    private getInsertionCycles() {
        this._logger.info("Get Insertion Cycles(): Retrieving list of Insertion Cycles");
        this.isLoadingInsertionCycles = true;
        this.insertionCyclesService.getInsertionCycles(this.tableConfig).subscribe(
            (data: any) => {
                this._logger.info("Get insertion Cycles(): API Returned", data);
                this.insertionCycles = data.items;
                for (const insertionCycle of this.insertionCycles) {
                    insertionCycle.assignedLinesString = insertionCycle.assignedLines.map(x => x.name).join(', ');
                }
                this.tableConfig.pager.count = data.total;
                this.isLoadingInsertionCycles = false;
            },
            (error: any) => {
                this._logger.info("Get Insertion Cycles(): Error retrieving data", error);
                this.isLoadingInsertionCycles = false;
            }
        )
    }

    onTableRowClick(insertionCycle: InsertionCycle) {
        this.selectedInsertionCycle = insertionCycle;
        this.insertionCycleModal.open(insertionCycle)
    }

    create(insertionCycle: InsertionCycle) {
        this.isModalLoading = true;
        this.insertionCyclesService.createInsertionCycle(insertionCycle).subscribe(
            (data: any) => {
                this.getInsertionCycles();
                this.toastr.success(this.buildCreateMessage(data.name));
                this.isModalLoading = false;
                this.insertionCycleModal.modal.close();
            },
            (error: any) => {
                this._logger.info("create(): Error creating data", error);
                this.isModalLoading = false;
            }
        );
    }

    update(insertionCycle: InsertionCycle) {
        this.isModalLoading = true;
        this.insertionCyclesService.updateInsertionCycle(insertionCycle).subscribe(
            (data: any) => {
                this.getInsertionCycles();
                this.toastr.success(this.buildCreateMessage(data.name));
                this.isModalLoading = false;
                this.insertionCycleModal.modal.close();
            },
            (error: any) => {
                this._logger.info("update(): Error updating data", error);
                this.toastr.error(this.buildErrorMessage(error))
                this.isModalLoading = false;
            }
        )
    }

    delete(insertionCycle: InsertionCycle) {
        this.isModalLoading = true;
        this.insertionCyclesService.deleteInsertionCycle(insertionCycle.id).subscribe(
            (data: any) => {
                this.getInsertionCycles();
                this.toastr.success(this.buildDeleteMessage(data));
                this.isModalLoading = false;
                this.insertionCycleModal.modal.close();
            },
            (error: any) => {
                this._logger.info("update(): Error updating data", error);
                this.toastr.error(this.buildErrorMessage(error))
                this.isModalLoading = false;
            }
        )
    }

    buildCreateMessage(insertionCycleName) {
        const message = this.translationService.instant('admin.insertion-cycles.toasts.created');
        return this.translateParser.interpolate(message, {name: insertionCycleName});
    }

    buildDeleteMessage(insertionCycleName) {
        const message = this.translationService.instant('admin.insertion-cycles.toasts.deleted');
        return this.translateParser.interpolate(message, {name: insertionCycleName});
    }

    buildUpdateMessage(insertionCycleName) {
        const message = this.translationService.instant('admin.insertion-cycles.toasts.updated');
        return this.translateParser.interpolate(message, {name: insertionCycleName});
    }

    buildErrorMessage(error) {
        const message = this.translationService.instant('admin.insertion-cycles.toasts.error');
        return this.translateParser.interpolate(message, {name: error});
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_INSERTION_CYCLES');
    }

}
